import { FCM } from "@capacitor-community/fcm";
import { PushNotifications } from "@capacitor/push-notifications";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Button, Toast } from "react-bootstrap";
import { Bell, BellFill } from 'react-bootstrap-icons';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Capacitor } from "@capacitor/core";

function NotificationButtonNotificationPage({ notificationStorage, onToggle }) {
  const [active, setActive] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Re-read from localStorage whenever notificationStorage prop changes
  // This is the KEY FIX - ensures state updates when switching countries
  useEffect(() => {
    const storedValue = localStorage.getItem(notificationStorage);
    const newActive = storedValue === 'true';
    // console.log(`[${notificationStorage}] Reading from localStorage: ${storedValue} -> active: ${newActive}`);
    setActive(newActive);
  }, [notificationStorage]);
  
  const topic = useMemo(() => {
    return notificationStorage; // Use storage key directly as FCM topic
  }, [notificationStorage]);
  
  const buttonText = useMemo(() => {
    return active ? 'On' : 'Off';
  }, [active]);
  
  // Memoized notification type checks
  const notificationTypes = useMemo(() => {
    const stateNotifications = [
      "alabamaNews", "alaskaNews", "arizonaNews", "arkansasNews",
      "californiaNews", "coloradoNews", "connecticutNews", "delawareNews",
      "floridaNews", "georgiaNews", "hawaiiNews", "idahoNews",
      "illinoisNews", "indianaNews", "iowaNews", "kansasNews",
      "kentuckyNews", "louisianaNews", "maineNews", "marylandNews",
      "massachusettsNews", "michiganNews", "minnesotaNews", "mississippiNews",
      "missouriNews", "montanaNews", "nebraskaNews", "nevadaNews",
      "newHampshireNews", "newJerseyNews", "newMexicoNews", "newYorkNews",
      "northCarolinaNews", "northDakotaNews", "ohioNews", "oklahomaNews",
      "oregonNews", "pennsylvaniaNews", "rhodeIslandNews", "southCarolinaNews",
      "southDakotaNews", "tennesseeNews", "texasNews", "utahNews",
      "vermontNews", "virginiaNews", "washingtonNews", "westVirginiaNews",
      "wisconsinNews", "wyomingNews"
    ];
    
    const provinceNotifications = [
      "albertaUpdates", "britishColumbiaUpdates", "manitobaUpdates", "newBrunswickUpdates",
      "newfoundlandAndLabradorUpdates", "northwestTerritoriesUpdates", "novaScotiaUpdates", 
      "nunavutUpdates", "ontarioUpdates", "princeEdwardIslandUpdates", "quebecUpdates", 
      "saskatchewanUpdates", "yukonUpdates"
    ];
    
    const ukRegionNotifications = [
      "englandUpdates", "scotlandUpdates", "walesUpdates", "northernIrelandUpdates"
    ];
    
    const mexicoStateNotifications = [
      "aguascalientesUpdates", "bajaCaliforniaUpdates", "bajaCaliforniaSurUpdates", 
      "campecheUpdates", "chiapasUpdates", "chihuahuaUpdates", "coahuilaUpdates", 
      "colimaUpdates", "durangoUpdates", "guanajuatoUpdates", "guerreroUpdates", 
      "hidalgoUpdates", "jaliscoUpdates", "mexicoStateUpdates", "mexicoCityUpdates", 
      "michoacanUpdates", "morelosUpdates", "nayaritUpdates", "nuevoLeonUpdates", 
      "oaxacaUpdates", "pueblaUpdates", "queretaroUpdates", "quintanaRooUpdates", 
      "sanLuisPotosiUpdates", "sinaloaUpdates", "sonoraUpdates", "tabascoUpdates", 
      "tamaulipasUpdates", "tlaxcalaUpdates", "veracruzUpdates", "yucatanUpdates", 
      "zacatecasUpdates"
    ];

    return {
      isState: stateNotifications.includes(notificationStorage),
      isProvince: provinceNotifications.includes(notificationStorage),
      isUKRegion: ukRegionNotifications.includes(notificationStorage),
      isMexicoState: mexicoStateNotifications.includes(notificationStorage)
    };
  }, [notificationStorage]);

  const handleClick = useCallback(async () => {
    try {
      await Haptics.impact({ style: ImpactStyle.Light });
    } catch (error) {
      // Haptics not available, continue silently
    }
    
    const newActive = !active;
    setActive(newActive);
    localStorage.setItem(notificationStorage, String(newActive));
  
    // Notify parent component about the change
    if (onToggle) {
      onToggle(notificationStorage, newActive);
    }
  
    // Dispatch storage event to trigger other components to update
    window.dispatchEvent(new Event("storage"));

    // Only handle FCM subscriptions on native platforms
    if (!Capacitor.isNativePlatform()) {
      // console.log(`[${notificationStorage}] Web platform - saved to localStorage: ${newActive}`);
      return;
    }
  
    if (newActive) {
      // Subscribe
      try {
        const permissions = await PushNotifications.checkPermissions();
        if (permissions.receive === 'granted') {
          await FCM.subscribeTo({ topic });
          // console.log(`[${notificationStorage}] Subscribed to FCM topic: ${topic}`);
          
          // Set home value for regional notifications
          if (notificationTypes.isState) {
            const stateKey = notificationStorage.replace('News', '');
            localStorage.setItem('homeState', stateKey);
          } else if (notificationTypes.isProvince) {
            const provinceKey = notificationStorage.replace('Updates', '');
            localStorage.setItem('homeProvince', provinceKey);
          } else if (notificationTypes.isUKRegion) {
            const regionKey = notificationStorage.replace('Updates', '');
            localStorage.setItem('homeUKRegion', regionKey);
          } else if (notificationTypes.isMexicoState) {
            const stateKey = notificationStorage.replace('Updates', '');
            localStorage.setItem('homeMexicanState', stateKey);
          }
          
        } else {
          // console.log(`[${notificationStorage}] Permission denied`);
        }
      } catch (error) {
        console.error(`[${notificationStorage}] Subscribe error:`, error);
      }
    } else {
      // Unsubscribe
      try {
        await FCM.unsubscribeFrom({ topic });
        // console.log(`[${notificationStorage}] Unsubscribed from FCM topic: ${topic}`);
        
        // Clear home values for regional notifications
        if (notificationTypes.isState) {
          const stateKey = notificationStorage.replace('News', '');
          if (localStorage.getItem('homeState') === stateKey) {
            localStorage.setItem('homeState', '');
          }
        } else if (notificationTypes.isProvince) {
          const provinceKey = notificationStorage.replace('Updates', '');
          if (localStorage.getItem('homeProvince') === provinceKey) {
            localStorage.setItem('homeProvince', '');
          }
        } else if (notificationTypes.isUKRegion) {
          const regionKey = notificationStorage.replace('Updates', '');
          if (localStorage.getItem('homeUKRegion') === regionKey) {
            localStorage.setItem('homeUKRegion', '');
          }
        } else if (notificationTypes.isMexicoState) {
          const stateKey = notificationStorage.replace('Updates', '');
          if (localStorage.getItem('homeMexicanState') === stateKey) {
            localStorage.setItem('homeMexicanState', '');
          }
        }
        
      } catch (error) {
        console.error(`[${notificationStorage}] Unsubscribe error:`, error);
      }
    }
  }, [active, notificationStorage, topic, onToggle, notificationTypes]);

  const register = useCallback(() => {
    if (!Capacitor.isNativePlatform() || isInitialized) {
      return;
    }

    PushNotifications.register();

    const listeners = [
      PushNotifications.addListener('registration', (token) => {
        // console.log('Push registration success');
      }),

      PushNotifications.addListener('registrationError', (error) => {
        console.error('Push registration error:', error);
      }),

      PushNotifications.addListener('pushNotificationReceived', (notification) => {
        setNotifications((prev) => [
          ...prev,
          { id: notification.id, title: notification.title, body: notification.body, type: 'foreground' },
        ]);
      }),

      PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
        setNotifications((prev) => [
          ...prev,
          { 
            id: notification.notification.data.id, 
            title: notification.notification.data.title, 
            body: notification.notification.data.body, 
            type: 'action' 
          },
        ]);
      })
    ];

    return () => {
      listeners.forEach(listener => listener.remove());
    };
  }, [isInitialized]);

  // Initialize FCM registration once
  useEffect(() => {
    if (isInitialized) return;
    const cleanup = register();
    setIsInitialized(true);
    return cleanup;
  }, [register, isInitialized]);

  return (
    <Button id='notificationButtonPage' onClick={handleClick}>
      {active ? <BellFill size={18} className="me-2" /> : <Bell size={18} className="me-2" />}
      {buttonText}
    </Button>
  );
}

export default React.memo(NotificationButtonNotificationPage);